import { PlatformContextType } from "@wingspanhq/users/dist/lib/interfaces";
import { UserAccountType } from "@wingspanhq/users/dist/lib/interfaces/newUser";
import { useCustomization } from "../../modules/customization";
import {
  IFeatureFlags,
  useQueryFeatureFlags
} from "@hooks/useQueryFeatureFlags";
import { useShowBatchImportsNav } from "../../userFeatures/useShowBatchImportsNav";
import { useShowCustomFieldsNav } from "../../userFeatures/useShowCustomFieldsNav";
import { useShowGetPaidNav } from "../../userFeatures/useShowGetPaidNav";
import { useShowNewCollaboratorPaymentNav } from "../../userFeatures/useShowNewCollaboratorPaymentNav";
import { useShowNewOneTimeInvoiceNav } from "../../userFeatures/useShowNewOneTimeInvoiceNav";
import { useShowNewRecurringInvoicesNav } from "../../userFeatures/useShowNewRecurringInvoicesNav";
import { useShowPayeableAndPayrollSettingsNav } from "../../userFeatures/useShowPayeableAndPayrollSettingsNav";
import { useShowPaymentNav } from "../../userFeatures/useShowPaymentNav";
import { useShowPayoutNav } from "../../userFeatures/useShowPayoutNav";
import { useShowSendPaymentsNav } from "../../userFeatures/useShowSendPaymentsNav";
import { useAccountAccess } from "../../v3/hooks/useAccountAccess";

export type DashboardFocusedFeatureContext = {
  experiments: IFeatureFlags;
  hardcoded: {
    showBatchImportsNav: boolean;
    showCustomFieldsNav: boolean;
    showGetPaidNav: boolean;
    showNewCollaboratorPaymentNav: boolean;
    showNewOneTimeInvoiceNav: boolean;
    showNewRecurringInvoicesNav: boolean;
    showPayableAndPayrollSettingsNav: boolean;
    showPaymentNav: boolean;
    showPayoutNav: boolean;
    showSendPaymentsNav: boolean;
  };
  session: {
    isImpersonated: boolean;
  };
  user: {
    country: string;
    contexts: PlatformContextType[];
    userType?: UserAccountType;
    labels?: { [label: string]: string | boolean | null };
    payersCount: number;
    payersWithWorkLoggingCount: number;
    workDefinitionsCount: number;
  };
  access: {
    hasScope: (scope: string) => boolean;
  };
  customization: ReturnType<typeof useCustomization>;
};

export const useGetDashboardFocusedFeaturesContext =
  (): DashboardFocusedFeatureContext | null => {
    const featureFlagsQuery = useQueryFeatureFlags({
      refetchOnMount: false
    });

    // The v1 user/member/payer-rows-summary/work-definitions reads were removed
    // during the v3 migration — they threw NotMigrated and blanked the entire
    // nav (this builder returned null when their data was missing). Access is
    // now sourced from the v3 authorizations (useAccountAccess); the remaining
    // `user.*` fields below could not be matched to v3 yet and are defaulted.

    const customization = useCustomization();

    const hardcoded = {
      showBatchImportsNav: useShowBatchImportsNav(),
      showCustomFieldsNav: useShowCustomFieldsNav(),
      showGetPaidNav: useShowGetPaidNav(),
      showNewCollaboratorPaymentNav: useShowNewCollaboratorPaymentNav(),
      showNewOneTimeInvoiceNav: useShowNewOneTimeInvoiceNav(),
      showNewRecurringInvoicesNav: useShowNewRecurringInvoicesNav(),
      showPayableAndPayrollSettingsNav: useShowPayeableAndPayrollSettingsNav(),
      showPaymentNav: useShowPaymentNav(),
      showPayoutNav: useShowPayoutNav(),
      showSendPaymentsNav: useShowSendPaymentsNav()
    };

    const { isImpersonated, hasScope } = useAccountAccess();

    if (!featureFlagsQuery.data) {
      return null;
    }

    const experiments = featureFlagsQuery.data;

    return {
      hardcoded,
      experiments,
      user: {
        // country — was v1 member.profile.address.country. No v3 wiring yet;
        // defaulted to US (it only gates the tax-filing section, which also
        // depends on the enterprise/context flags below).
        country: "US",
        // contexts — was v1 user.contexts (Payer/Payee/TaxOnly). No v3
        // equivalent; nav should rely on access.hasScope instead.
        contexts: [],
        // userType — was v1 user.settings.userAccountType (enterprise). No v3
        // equivalent.
        userType: undefined,
        // labels — was v1 user.labels. No v3 equivalent.
        labels: undefined,
        // payersCount / payersWithWorkLoggingCount — were v1 payer-rows summary.
        // Derivable from v3 queriesPayers.listPayers later; defaulted for now.
        payersCount: 0,
        payersWithWorkLoggingCount: 0,
        // workDefinitionsCount — was v1 work definitions. v3 source TBD.
        workDefinitionsCount: 0
      },
      session: {
        isImpersonated
      },
      access: {
        hasScope
      },
      customization
    };
  };
